import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextareaAutosize, Input, Typography } from "@mui/material";
import { PencilIcon, Plus, Sparkles, Workflow } from "lucide-react";
import { ButtonGroup as ModeSelection } from "../../../components/ui/button-group";

import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";
import { usePlaygroundParametersStore } from "@/atoms/playground";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ModelMenu from "./ModelMenu";
import { SliderWithLabel } from "@/components/ui/slider-with-label";
import BlueprintAgentToolsPicker from "./BlueprintAgentToolsPicker";
import BlueprintAttachments from "./BlueprintAttachments";
import {
  useDefaultModelsQuery,
  useModelsQuery,
} from "@/features/playground/hooks/useModelQueries";
import { Skeleton } from "@/components/ui/skeleton";
const modes = [
  {
    value: "agentic",
    labelKey: "playground.agentic",
    icon: <Sparkles className="h-3 w-3" />,
    tooltipKey: "playground.agenticTooltip",
  },
  {
    value: "planner",
    labelKey: "playground.planner",
    icon: <Workflow className="h-3 w-3" />,
    tooltipKey: "playground.plannerTooltip",
  },
];

export type BlueprintDetailsDialogProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmit?: (values: FullBlueprintFormValues) => void;
  blueprint?: {
    id: string;
    name: string;
    description: string;
    instructions?: string;
    default_tools?: string[];
    default_document_ids?: string[];
    default_collection_ids?: string[];
  };
};

const fullBlueprintSchema = z.object({
  name: z.string().min(1, "Name is required"),
  instructions: z.string().min(1, "Instructions are required"),
  description: z.string().optional(),
  default_base_llm_config_id: z.string(),
  default_planner_llm_config_id: z.string().optional(),
  default_mode: z.string(),
  default_language: z.string(),
  default_tools: z.array(z.string()).optional(),
  default_temperature: z.number().optional(),
  default_max_tokens: z.number().optional(),
  default_document_ids: z.array(z.string()).optional(),
  default_collection_ids: z.array(z.string()).optional(),
  // Add other fields as necessary
});

type FullBlueprintFormValues = z.infer<typeof fullBlueprintSchema>;

const BlueprintDetailsDialog = ({
  open,
  onOpenChange,
  onSubmit,
  blueprint,
}: BlueprintDetailsDialogProps) => {
  const { t, i18n } = useTranslation();
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const {
    llm_config_id,
    planner_llm_config,
    mode,
    temperature,
    max_tokens,
    language,
  } = usePlaygroundParametersStore();
  const modelsQuery = useModelsQuery();
  const defaultModelsQuery = useDefaultModelsQuery();
  const models = modelsQuery.data;
  const defaultSelectedModels = defaultModelsQuery.data;

  const languages = [
    { value: "auto", label: t("playground.auto") },
    { value: "en", label: t("playground.english") },
    { value: "ar", label: t("playground.arabic") },
  ];

  const {
    register,
    clearErrors,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FullBlueprintFormValues>({
    resolver: zodResolver(fullBlueprintSchema),
    defaultValues: {
      name: blueprint?.name || "",
      instructions: blueprint?.instructions || "",
      description: blueprint?.description || "",
      default_base_llm_config_id: llm_config_id || "",
      default_planner_llm_config_id: planner_llm_config || "",
      default_mode: mode || "agentic",
      default_language: language || "",
      default_tools: blueprint?.default_tools || [],
      default_temperature: temperature,
      default_max_tokens: max_tokens,
      default_document_ids: blueprint?.default_document_ids || [],
      default_collection_ids: blueprint?.default_collection_ids || [],
    },
  });
  const model = models?.find(
    (model) => model.id === watch("default_base_llm_config_id"),
  );
  const selectedBaseModelId = watch("default_base_llm_config_id");
  const selectedPlannerModelId = watch("default_planner_llm_config_id");
  const selectedMode = watch("default_mode");
  const isModelSelectionReady =
    modelsQuery.ready &&
    modelsQuery.isSuccess &&
    models?.some((candidate) => candidate.id === selectedBaseModelId) &&
    (selectedMode !== "planner" ||
      models.some((candidate) => candidate.id === selectedPlannerModelId));
  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!isModelSelectionReady) return;
      onSubmit?.(getValues());
      onOpenChange?.(false);
    },
    [getValues, isModelSelectionReady, onOpenChange, onSubmit],
  );
  const handleDiscard = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-y-auto sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t(`blueprints.BlueprintContext`)}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="blueprint-name"
              className="text-foreground text-sm font-semibold"
            >
              {t("blueprints.fields.name")}
            </label>
            <Input
              id="blueprint-name"
              disableUnderline
              placeholder={t("blueprints.fields.namePlaceholder")}
              className={`bg-input text-foreground placeholder:text-muted-foreground h-10 rounded-[8px] px-3 py-2 ${
                errors.name
                  ? "border-destructive focus-visible:ring-destructive/20"
                  : "border-border"
              }`}
              aria-invalid={Boolean(errors.name)}
              {...register("name", {
                onChange: (event) => {
                  if (event.target.value.trim()) {
                    clearErrors("name");
                  }
                },
              })}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <div>
                <Typography
                  variant="body2"
                  className="text-foreground text-sm font-semibold"
                >
                  {t("blueprints.instructions")}
                </Typography>
                <Typography
                  variant="body2"
                  className="text-sm text-muted-foreground"
                >
                  {t(
                    "blueprints.fields.instructionsDescription",
                    "Add instructions to tailor the blueprint's responses",
                  )}
                </Typography>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setInstructionsDialogOpen(true)}
                className="size-6 rounded-[8px] bg-secondary text-foreground hover:bg-secondary/80"
                aria-label={t(
                  "blueprints.fields.addInstructions",
                  "Add instructions",
                )}
              >
                {watch("instructions") ? (
                  <PencilIcon className="size-4" />
                ) : (
                  <Plus className="size-4" />
                )}
              </Button>
            </div>
            {watch("instructions") && (
              <p className="text-sm text-muted-foreground pt-3">
                {watch("instructions")}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <div>
              <Typography
                variant="body2"
                className="text-foreground text-sm font-semibold"
              >
                Configurations
              </Typography>
              <Typography
                variant="body2"
                className="text-sm text-muted-foreground"
              >
                Tune the blueprint’s model preferences
              </Typography>
            </div>

            <div className="grid *:grid-cols-1 gap-4 sm:grid-cols-2 pt-3">
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="default-mode"
                  className="text-sm text-muted-foreground"
                >
                  Default Mode
                </label>
                <ModeSelection
                  value={watch("default_mode")}
                  onValueChange={(value) => {
                    setValue("default_mode", value);
                  }}
                  options={modes.map((mode) => ({
                    ...mode,
                    label: t(mode.labelKey),
                    icon: mode.icon, // Correctly passing the icon property
                    tooltip: t(mode.tooltipKey), // Passing the tooltip property
                  }))}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="blueprint-language"
                  className="text-sm text-muted-foreground"
                >
                  Preferred Language
                </label>

                <Select
                  dir={i18n.dir()}
                  value={watch("default_language")}
                  onValueChange={(value) => setValue("default_language", value)}
                >
                  <SelectTrigger className="w-full bg-input text-foreground placeholder:text-muted-foreground h-11 rounded-[8px] px-4 py-3 border border-border">
                    <SelectValue placeholder={t("playground.auto")} />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((language) => (
                      <SelectItem key={language.value} value={language.value}>
                        {language.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="blueprint-base-llm-config"
                  className="text-sm text-muted-foreground"
                >
                  Base Model
                </label>

                {!modelsQuery.ready || modelsQuery.isPending ? (
                  <Skeleton className="h-11 w-full" />
                ) : (
                  <ModelMenu
                    type="base"
                    models={models}
                    selectedModel={watch("default_base_llm_config_id")}
                    defaultModels={defaultSelectedModels?.defaults}
                    onModelChange={(modelId) =>
                      setValue("default_base_llm_config_id", modelId)
                    }
                  />
                )}
              </div>

              {getValues("default_mode") === "planner" && (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="blueprint-planner-llm-config"
                    className="text-sm text-muted-foreground"
                  >
                    Planner Model
                  </label>

                  {!modelsQuery.ready || modelsQuery.isPending ? (
                    <Skeleton className="h-11 w-full" />
                  ) : (
                    <ModelMenu
                      type="planner"
                      models={models}
                      selectedModel={watch("default_planner_llm_config_id")}
                      defaultModels={defaultSelectedModels?.defaults}
                      onModelChange={(modelId) =>
                        setValue("default_planner_llm_config_id", modelId)
                      }
                    />
                  )}
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <SliderWithLabel
                  label={t("playground.temperature")}
                  min={0}
                  max={1}
                  step={0.01}
                  value={watch("default_temperature") || 0}
                  onChange={(value) => setValue("default_temperature", value)}
                  padding="0px 4px 0px 0px"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <SliderWithLabel
                  label={t("playground.maximumTokens")}
                  min={0}
                  max={model?.max_tokens || 4096}
                  step={Math.max(
                    1,
                    Math.floor((model?.max_tokens || 4096) / 100),
                  )}
                  value={watch("default_max_tokens") || 0}
                  onChange={(value) => setValue("default_max_tokens", value)}
                  padding="0px 4px 0px 0px"
                />
              </div>
            </div>
            {(modelsQuery.isError || defaultModelsQuery.isError) && (
              <p role="alert" className="text-sm text-destructive">
                {t("aiCatalog.failedToLoadModels")}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <BlueprintAgentToolsPicker
              selectedTools={watch("default_tools")}
              selectedCollectionIds={watch("default_collection_ids")}
              selectedDocumentIds={watch("default_document_ids")}
              onChange={({ tools, collectionIds, documentIds }) => {
                setValue("default_tools", tools);
                setValue("default_collection_ids", collectionIds);
                setValue("default_document_ids", documentIds);
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <BlueprintAttachments />
          </div>

          <DialogFooter className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscard}
              className="border-border bg-card text-foreground hover:bg-accent px-6"
            >
              {t("common.discard")}
            </Button>
            <Button
              type="submit"
              disabled={!isModelSelectionReady}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-6"
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>

        <Dialog
          open={instructionsDialogOpen}
          onOpenChange={setInstructionsDialogOpen}
        >
          <DialogContent className="sm:max-w-[560px]">
            <DialogHeader>
              <DialogTitle>
                {t("blueprints.fields.instructions", "Instructions")}
              </DialogTitle>
            </DialogHeader>
            <TextareaAutosize
              id="blueprint-instructions"
              minRows={8}
              autoFocus
              placeholder={t("blueprints.fields.instructionsPlaceholder")}
              className={`bg-input text-foreground placeholder:text-muted-foreground w-full rounded-[8px] border px-4 py-3 text-sm outline-hidden focus:border-primary ${
                errors.instructions ? "border-destructive" : "border-border"
              }`}
              aria-invalid={Boolean(errors.instructions)}
              {...register("instructions")}
            />
            {errors.instructions && (
              <p className="text-sm text-red-500">
                {errors.instructions.message}
              </p>
            )}
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="blueprint-description"
                className="text-foreground text-sm font-semibold"
              >
                {t("blueprints.fields.description", "Description")}
              </label>
              <TextareaAutosize
                id="blueprint-description"
                minRows={3}
                placeholder={t(
                  "blueprints.fields.descriptionPlaceholder",
                  "Describe what this blueprint is used for",
                )}
                className={`bg-input text-foreground placeholder:text-muted-foreground w-full rounded-[8px] border px-4 py-3 text-sm outline-hidden focus:border-primary ${
                  errors.description ? "border-destructive" : "border-border"
                }`}
                aria-invalid={Boolean(errors.description)}
                {...register("description")}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setInstructionsDialogOpen(false)}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                type="button"
                onClick={() => setInstructionsDialogOpen(false)}
              >
                {t("common.done", "Done")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default BlueprintDetailsDialog;
